import { ArrowLeft } from 'lucide-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@doger/ui/components/ui/card';
import { Link } from '@doger/ui/components/ui/link';

import { useAuthContext } from '../auth.context';
import { ResetPasswordForm } from './reset-password-form.component';
import { isResetTokenValid } from './reset-password-token-decoder';
import styles from './reset-password-page.module.scss';

type ResetPasswordPageProps = {
  onSuccess?: () => void;
};

export const ResetPasswordPage = memo(
  ({ onSuccess }: ResetPasswordPageProps) => {
    const [searchParams] = useSearchParams();
    const resetToken = searchParams.get('token');
    const { signInUrl } = useAuthContext();
    const { t } = useTranslation('core');

    if (!resetToken || !isResetTokenValid(resetToken)) {
      return (
        <div className={styles.container}>
          <Card className={styles.card}>
            <CardHeader>
              <CardTitle>{t('auth.resetPassword.invalidLinkTitle')}</CardTitle>
              <CardDescription>
                {t('auth.resetPassword.invalidLinkDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={styles.links}>
                <Link className={styles.link} to={signInUrl}>
                  <ArrowLeft />
                  {t('auth.navigation.backToSignIn')}
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <CardHeader>
            <CardTitle>{t('auth.resetPassword.title')}</CardTitle>
            <CardDescription>
              {t('auth.resetPassword.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResetPasswordForm token={resetToken} onSuccess={onSuccess} />

            <div className={styles.links}>
              <Link className={styles.link} to={signInUrl}>
                <ArrowLeft />
                {t('auth.navigation.backToSignIn')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
);

ResetPasswordPage.displayName = 'ResetPasswordPage';
